// ---------------------------------------------------------------------------
// useAttachments — owns prompt-attachment state + all input wiring (paste,
// drag-drop, file picker). Shared by every "Ask AI" prompt surface.
//
//   const att = useAttachments()
//   <AttachmentChips attachments={att.attachments} onRemove={att.remove} />
//   <textarea {...att.inputHandlers} />
//   <input type="file" ref={att.fileInputRef} onChange={att.onFileInputChange} multiple />
//   <button onClick={att.openFilePicker}><Paperclip/></button>
//   ... on submit: send att.attachments.map(toWire); then att.clear()
// ---------------------------------------------------------------------------
import {
  useCallback,
  useRef,
  useState,
  type ClipboardEvent,
  type DragEvent,
  type ChangeEvent,
  type RefObject,
} from 'react'
import toast from 'react-hot-toast'
import type { Attachment } from '@/types'
import {
  MAX_ATTACHMENTS,
  MAX_TEXT_BYTES,
  MAX_IMAGE_BYTES,
  MAX_DOC_BYTES,
  MAX_SHEET_BYTES,
  MAX_DESIGN_BYTES,
  MAX_TOTAL_IMAGE_BYTES,
  MAX_TOTAL_BINARY_BYTES,
  LARGE_PASTE_THRESHOLD,
  classifyKind,
  readFileAsAttachment,
  makeTextAttachment,
  formatBytes,
} from '@/lib/attachments'

export interface UseAttachments {
  attachments: Attachment[]
  clear: () => void
  remove: (id: string) => void
  addFiles: (files: FileList | File[] | null | undefined) => Promise<void>
  addText: (text: string, name?: string) => void
  inputHandlers: {
    onPaste: (e: ClipboardEvent) => void
    onDrop: (e: DragEvent) => void
    onDragOver: (e: DragEvent) => void
  }
  fileInputRef: RefObject<HTMLInputElement | null>
  openFilePicker: () => void
  onFileInputChange: (e: ChangeEvent<HTMLInputElement>) => void
}

export interface UseAttachmentsOptions {
  /** Accept a Claude Design export (.zip) — only where the backend imports it
   *  (the Ask-AI dock). Elsewhere a .zip is refused with a clear message. */
  allowDesign?: boolean
}

export function useAttachments(options: UseAttachmentsOptions = {}): UseAttachments {
  const allowDesign = !!options.allowDesign
  const [attachments, setAttachments] = useState<Attachment[]>([])
  const fileInputRef = useRef<HTMLInputElement | null>(null)

  const clear = useCallback(() => setAttachments([]), [])

  const remove = useCallback((id: string) => {
    setAttachments((prev) => prev.filter((a) => a.id !== id))
  }, [])

  const addFiles = useCallback(async (files: FileList | File[] | null | undefined) => {
    if (!files) return
    const list = Array.from(files)
    if (list.length === 0) return

    // Read everything that passes per-file caps; the count/total caps are
    // enforced atomically against the latest state in the setter below.
    const accepted: Attachment[] = []
    for (const file of list) {
      const kind = classifyKind(file, { allowDesign })
      if (!kind) {
        toast.error(`Unsupported file: ${file.name || 'unnamed'}`)
        continue
      }
      if (kind === 'design' && file.size > MAX_DESIGN_BYTES) {
        toast.error(`${file.name} is too large (max ${formatBytes(MAX_DESIGN_BYTES)})`)
        continue
      }
      if (kind === 'text' && file.size > MAX_TEXT_BYTES) {
        toast.error(`${file.name} is too large (max ${formatBytes(MAX_TEXT_BYTES)})`)
        continue
      }
      if (kind === 'image' && file.size > MAX_IMAGE_BYTES) {
        toast.error(`${file.name} is too large (max ${formatBytes(MAX_IMAGE_BYTES)})`)
        continue
      }
      if (kind === 'document' && file.size > MAX_DOC_BYTES) {
        toast.error(`${file.name} is too large (max ${formatBytes(MAX_DOC_BYTES)})`)
        continue
      }
      if (kind === 'spreadsheet' && file.size > MAX_SHEET_BYTES) {
        toast.error(`${file.name} is too large (max ${formatBytes(MAX_SHEET_BYTES)})`)
        continue
      }
      try {
        accepted.push(await readFileAsAttachment(file, { allowDesign }))
      } catch (err) {
        toast.error(err instanceof Error ? err.message : `Failed to read ${file.name}`)
      }
    }
    if (accepted.length === 0) return

    setAttachments((prev) => {
      const next = [...prev]
      let hitCountCap = false
      let hitImageCap = false
      let hitBinaryCap = false
      const totalBytes = (list: Attachment[], match: (a: Attachment) => boolean) =>
        list.filter(match).reduce((s, a) => s + a.size, 0)
      for (const att of accepted) {
        if (next.length >= MAX_ATTACHMENTS) {
          hitCountCap = true
          break
        }
        // Images keep their own sub-cap; images + documents share the
        // transport budget (the whole turn is one JSON/WS frame).
        if (att.kind === 'image') {
          if (totalBytes(next, (a) => a.kind === 'image') + att.size > MAX_TOTAL_IMAGE_BYTES) {
            hitImageCap = true
            continue
          }
        }
        if (att.kind !== 'text') {
          if (totalBytes(next, (a) => a.kind !== 'text') + att.size > MAX_TOTAL_BINARY_BYTES) {
            hitBinaryCap = true
            continue
          }
        }
        next.push(att)
      }
      if (hitCountCap) toast.error(`You can attach up to ${MAX_ATTACHMENTS} files`)
      if (hitImageCap) toast.error(`Total image size exceeds ${formatBytes(MAX_TOTAL_IMAGE_BYTES)}`)
      if (hitBinaryCap) {
        toast.error(
          `Total attachment size exceeds ${formatBytes(MAX_TOTAL_BINARY_BYTES)}`,
        )
      }
      return next
    })
  }, [allowDesign])

  const addText = useCallback((text: string, name?: string) => {
    setAttachments((prev) => {
      if (prev.length >= MAX_ATTACHMENTS) {
        toast.error(`You can attach up to ${MAX_ATTACHMENTS} files`)
        return prev
      }
      return [...prev, makeTextAttachment(text, name)]
    })
  }, [])

  const onPaste = useCallback(
    (e: ClipboardEvent) => {
      const dt = e.clipboardData
      if (!dt) return
      const files: File[] = []
      for (const item of Array.from(dt.items)) {
        if (item.kind === 'file') {
          const f = item.getAsFile()
          if (f) files.push(f)
        }
      }
      if (files.length > 0) {
        e.preventDefault()
        void addFiles(files)
        return
      }
      const text = dt.getData('text')
      if (text && text.length >= LARGE_PASTE_THRESHOLD) {
        e.preventDefault()
        addText(text, 'Pasted text')
      }
      // Short text → let the textarea handle a normal paste.
    },
    [addFiles, addText],
  )

  const onDragOver = useCallback((e: DragEvent) => {
    if (e.dataTransfer && Array.from(e.dataTransfer.types).includes('Files')) {
      e.preventDefault()
    }
  }, [])

  const onDrop = useCallback(
    (e: DragEvent) => {
      if (e.dataTransfer && e.dataTransfer.files && e.dataTransfer.files.length > 0) {
        e.preventDefault()
        void addFiles(e.dataTransfer.files)
      }
    },
    [addFiles],
  )

  const openFilePicker = useCallback(() => {
    fileInputRef.current?.click()
  }, [])

  const onFileInputChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      void addFiles(e.target.files)
      e.target.value = ''
    },
    [addFiles],
  )

  return {
    attachments,
    clear,
    remove,
    addFiles,
    addText,
    inputHandlers: { onPaste, onDrop, onDragOver },
    fileInputRef,
    openFilePicker,
    onFileInputChange,
  }
}
